import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface TaskLog {
    id: number;
    status: 'pending' | 'completed' | 'failed' | 'pending_verification';
    due_at: string;
    completed_at: string | null;
    completed_by: number | null;
    failed_by: number | null;
    verified_by: number | null;
    verified_at: string | null;
}

interface ContractTask {
    id: number;
    title: string;
    description: string | null;
    frequency: 'hourly' | 'daily' | 'weekly';
    due_time: string | null;
    penalty_amount: string;
    is_active: boolean;
    requires_verification: boolean;
    cooldown_days: number;
    cooldown_hours: number;
    cooldown_minutes: number;
    logs: TaskLog[];
}

const FREQ_LABELS: Record<string, string> = {
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
};

function timeRemaining(dueAt: string): string {
    const diff = new Date(dueAt).getTime() - Date.now();
    if (diff <= 0) return 'Overdue';
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
}

function formatDueTime(dueAt: string): string {
    return new Date(dueAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatDueDate(dueAt: string): string {
    const date = new Date(dueAt);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === now.toDateString()) {
        return `Today ${formatDueTime(dueAt)}`;
    }
    if (date.toDateString() === tomorrow.toDateString()) {
        return `Tomorrow ${formatDueTime(dueAt)}`;
    }
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ' ' + formatDueTime(dueAt);
}

interface ContractTasksProps {
    contractId: number;
    isDom: boolean;
}

export function ContractTasks({ contractId, isDom }: ContractTasksProps) {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({
        title: '',
        description: '',
        frequency: 'daily' as string,
        due_time: '09:00',
        penalty_amount: '10',
        requires_verification: false,
        cooldown_days: 0,
        cooldown_hours: 0,
        cooldown_minutes: 0,
    });

    const { data: tasksData } = useQuery({
        queryKey: ['tasks', contractId],
        queryFn: () => api.get<{ data: ContractTask[] }>(`/contracts/${contractId}/tasks`),
    });

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['tasks', contractId] });
        queryClient.invalidateQueries({ queryKey: ['contract', String(contractId)] });
        queryClient.invalidateQueries({ queryKey: ['ledger', String(contractId)] });
        queryClient.invalidateQueries({ queryKey: ['contracts'] });
    };

    const createMutation = useMutation({
        mutationFn: (data: unknown) => api.post(`/contracts/${contractId}/tasks`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks', contractId] });
            setShowCreate(false);
            setForm({ title: '', description: '', frequency: 'daily', due_time: '09:00', penalty_amount: '10', requires_verification: false, cooldown_days: 0, cooldown_hours: 0, cooldown_minutes: 0 });
        },
    });

    const completeMutation = useMutation({
        mutationFn: (taskId: number) => api.post(`/contracts/${contractId}/tasks/${taskId}/complete`, {}),
        onSuccess: () => {
            trackEvent('task_completed', { contract_id: contractId });
            queryClient.invalidateQueries({ queryKey: ['tasks', contractId] });
        },
    });

    const failMutation = useMutation({
        mutationFn: (taskId: number) => api.post(`/contracts/${contractId}/tasks/${taskId}/fail`, {}),
        onSuccess: () => {
            trackEvent('task_failed', { contract_id: contractId });
            invalidateAll();
        },
    });

    const verifyMutation = useMutation({
        mutationFn: (taskId: number) => api.post(`/contracts/${contractId}/tasks/${taskId}/verify`, {}),
        onSuccess: () => {
            trackEvent('task_verified', { contract_id: contractId });
            queryClient.invalidateQueries({ queryKey: ['tasks', contractId] });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: (taskId: number) => api.post(`/contracts/${contractId}/tasks/${taskId}/reject`, {}),
        onSuccess: () => {
            trackEvent('task_rejected', { contract_id: contractId });
            invalidateAll();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (taskId: number) => api.delete(`/contracts/${contractId}/tasks/${taskId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks', contractId] }),
    });

    const tasks = tasksData?.data ?? [];

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">Tasks</CardTitle>
                {isDom && (
                    <Button size="sm" variant="outline" onClick={() => setShowCreate(!showCreate)}>
                        {showCreate ? 'Cancel' : '+ Add Task'}
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {showCreate && (
                    <div className="space-y-3 rounded border border-slate-700 p-3">
                        <Input
                            placeholder="Task title"
                            value={form.title}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                        />
                        <textarea
                            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 placeholder-slate-500"
                            rows={2}
                            placeholder="Description (optional)"
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs text-slate-400">Frequency</label>
                                <select
                                    className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm text-slate-200"
                                    value={form.frequency}
                                    onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                                >
                                    <option value="hourly">Hourly</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Penalty ($)</label>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.penalty_amount}
                                    onChange={e => setForm(f => ({ ...f, penalty_amount: e.target.value }))}
                                />
                            </div>
                        </div>

                        {form.frequency !== 'hourly' && (
                            <div>
                                <label className="text-xs text-slate-400">Due by time</label>
                                <Input
                                    type="time"
                                    value={form.due_time}
                                    onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))}
                                />
                                <p className="text-[11px] text-slate-500 mt-0.5">Fixed time the task is due each {form.frequency === 'daily' ? 'day' : 'week'}</p>
                            </div>
                        )}

                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.requires_verification}
                                onChange={e => setForm(f => ({ ...f, requires_verification: e.target.checked }))}
                                className="rounded"
                            />
                            <span className="text-slate-200">Require verification</span>
                            <span className="text-[11px] text-slate-500">— owner must confirm completion</span>
                        </label>

                        <div>
                            <label className="text-xs text-slate-400">Cooldown after completion</label>
                            <div className="grid grid-cols-3 gap-2 mt-1">
                                <div>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="365"
                                        value={form.cooldown_days}
                                        onChange={e => setForm(f => ({ ...f, cooldown_days: parseInt(e.target.value) || 0 }))}
                                    />
                                    <span className="text-[10px] text-slate-500">days</span>
                                </div>
                                <div>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="23"
                                        value={form.cooldown_hours}
                                        onChange={e => setForm(f => ({ ...f, cooldown_hours: parseInt(e.target.value) || 0 }))}
                                    />
                                    <span className="text-[10px] text-slate-500">hours</span>
                                </div>
                                <div>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={form.cooldown_minutes}
                                        onChange={e => setForm(f => ({ ...f, cooldown_minutes: parseInt(e.target.value) || 0 }))}
                                    />
                                    <span className="text-[10px] text-slate-500">minutes</span>
                                </div>
                            </div>
                        </div>

                        <Button
                            size="sm"
                            disabled={!form.title || createMutation.isPending}
                            onClick={() => createMutation.mutate({
                                title: form.title,
                                description: form.description || null,
                                frequency: form.frequency,
                                due_time: form.frequency !== 'hourly' ? form.due_time : null,
                                penalty_amount: parseFloat(form.penalty_amount),
                                requires_verification: form.requires_verification,
                                cooldown_days: form.cooldown_days,
                                cooldown_hours: form.cooldown_hours,
                                cooldown_minutes: form.cooldown_minutes,
                            })}
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Task'}
                        </Button>
                    </div>
                )}

                {tasks.length === 0 && !showCreate && (
                    <p className="text-sm text-slate-500 italic">No tasks configured.</p>
                )}

                {tasks.map(task => {
                    const pendingLog = task.logs.find(l => l.status === 'pending');
                    const verificationLogs = task.logs.filter(l => l.status === 'pending_verification');
                    const completedCount = task.logs.filter(l => l.status === 'completed').length;
                    const failedCount = task.logs.filter(l => l.status === 'failed').length;

                    return (
                        <div key={task.id} className="rounded border border-slate-700 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{task.title}</span>
                                    <Badge variant="outline" className="text-[10px]">
                                        {FREQ_LABELS[task.frequency]}
                                    </Badge>
                                    {task.requires_verification && (
                                        <Badge variant="outline" className="text-[10px] border-amber-700 text-amber-400">
                                            Verification
                                        </Badge>
                                    )}
                                    <Badge variant={task.is_active ? 'default' : 'secondary'}>
                                        {task.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>
                                {isDom && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-400 text-xs"
                                        onClick={() => deleteMutation.mutate(task.id)}
                                    >
                                        Delete
                                    </Button>
                                )}
                            </div>
                            {task.description && (
                                <p className="text-xs text-slate-400">{task.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                                <span>Penalty: {formatCurrency(parseFloat(task.penalty_amount))}</span>
                                <span className="text-green-400">Done: {completedCount}</span>
                                <span className="text-red-400">Failed: {failedCount}</span>
                                {task.due_time && (
                                    <span>Due by: {task.due_time}</span>
                                )}
                            </div>

                            {/* Pending verification logs — dom can verify/reject */}
                            {verificationLogs.map(vLog => (
                                <div key={vLog.id} className="flex items-center justify-between rounded bg-amber-950/30 border border-amber-800/40 p-2">
                                    <div className="text-xs">
                                        <Badge variant="outline" className="text-[10px] border-amber-700 text-amber-400 mr-2">
                                            Awaiting Verification
                                        </Badge>
                                        <span className="text-slate-400">
                                            Submitted {vLog.completed_at ? new Date(vLog.completed_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                        </span>
                                    </div>
                                    {isDom && (
                                        <div className="flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-green-800 text-green-400 hover:bg-green-900/30 text-xs h-7"
                                                disabled={verifyMutation.isPending}
                                                onClick={() => verifyMutation.mutate(task.id)}
                                            >
                                                Verify
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-red-800 text-red-400 hover:bg-red-900/30 text-xs h-7"
                                                disabled={rejectMutation.isPending}
                                                onClick={() => rejectMutation.mutate(task.id)}
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Current pending task */}
                            {pendingLog && (
                                <div className="flex items-center justify-between rounded bg-slate-800 p-2">
                                    <div className="text-xs">
                                        <span className="text-slate-400">Due: </span>
                                        <span className={new Date(pendingLog.due_at).getTime() < Date.now() ? 'text-red-400 font-medium' : 'text-slate-200'}>
                                            {formatDueDate(pendingLog.due_at)}
                                        </span>
                                        <span className="text-slate-500 ml-2">({timeRemaining(pendingLog.due_at)})</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-green-800 text-green-400 hover:bg-green-900/30 text-xs h-7"
                                            disabled={completeMutation.isPending}
                                            onClick={() => completeMutation.mutate(task.id)}
                                        >
                                            Complete
                                        </Button>
                                        {isDom && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-red-800 text-red-400 hover:bg-red-900/30 text-xs h-7"
                                                disabled={failMutation.isPending}
                                                onClick={() => failMutation.mutate(task.id)}
                                            >
                                                Fail
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
}
